import React, { useState, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
const { FiMail, FiAlertCircle, FiShield, FiLock, FiCpu, FiCheckCircle, FiActivity, FiClock, FiTrendingUp, FiZap, FiAlertTriangle, FiKey, FiUsers, FiBox, FiSend, FiLoader, FiRefreshCw } = FiIcons;

const KPI = ({ label, value, status, icon, loading }) => (
  <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center gap-4">
    <div className={`p-3 rounded-lg ${status === 'warning' ? 'bg-amber-50 text-amber-600' : status === 'error' ? 'bg-red-50 text-red-600' : 'bg-blue-50 text-blue-600'}`}>
      <SafeIcon icon={icon} className="text-xl" />
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest truncate">{label}</p>
      <div className="flex items-baseline gap-2">
        {loading ? (
          <div className="h-6 w-16 bg-slate-100 animate-pulse rounded mt-1"></div>
        ) : (
          <h4 className="text-xl font-bold text-slate-800 truncate">{value}</h4>
        )}
      </div>
    </div>
  </div>
);

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    clients: [],
    apps: [],
    keys: [],
    queue: [],
    providers: [],
    settings: []
  });

  const callGateway = async (action) => {
    try {
      const response = await fetch('/.netlify/functions/gateway', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action })
      });
      const result = await response.json();
      return result.success ? result.data : [];
    } catch (err) {
      console.error(`Dashboard fetch [${action}] failed`);
      return [];
    }
  };

  const fetchAll = async () => {
    setLoading(true);
    const [clients, apps, keys, queue, providers, settings] = await Promise.all([
      callGateway('list_clients'),
      callGateway('list_apps'),
      callGateway('list_api_keys'),
      callGateway('list_email_queue'),
      callGateway('list_providers'),
      callGateway('list_settings')
    ]);
    setData({ clients, apps, keys, queue, providers, settings });
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  const stats = {
    totalClients: data.clients.length,
    activeApps: data.apps.filter(a => a.status === 'active' || a.status === 'enabled').length,
    activeKeys: data.keys.filter(k => k.status === 'active' || k.status === 'enabled').length,
    queued: data.queue.filter(q => q.status === 'queued' || q.status === 'draft').length,
    sent: data.queue.filter(q => q.status === 'sent').length,
    failed: data.queue.filter(q => q.status === 'failed').length
  };

  const activeProvider = data.providers.find(p => p.priority === 1) || data.providers[0];

  // PB-10B: Calculate dynamic quota data
  const clientsWithQuotas = data.clients.map(client => {
    const clientKeys = data.keys.filter(k => k.client_id === client.id);
    const clientQueue = data.queue.filter(q => q.client_id === client.id);
    
    // Sum monthly limits from associated API Keys or Client record if exists
    const monthlyLimit = client.monthly_limit || clientKeys.reduce((sum, k) => sum + (Number(k.monthly_limit) || 0), 0);
    const sent = clientQueue.filter(q => q.status === 'sent').length;
    
    return {
      ...client,
      sent,
      monthlyLimit,
      percent: monthlyLimit > 0 ? Math.min(100, (sent / monthlyLimit) * 100) : 0
    };
  }).filter(c => c.monthlyLimit > 0);

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Operational Overview</h2>
          <p className="text-sm text-slate-500">Real-time gateway telemetry and resource status.</p>
        </div>
        <button onClick={fetchAll} className="p-2.5 bg-white border border-slate-200 text-slate-500 rounded-xl hover:bg-slate-50 shadow-sm transition-all">
          <SafeIcon icon={loading ? FiLoader : FiRefreshCw} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Provider Health Banner */}
      <div className="bg-slate-900 text-white rounded-2xl p-5 flex flex-col md:flex-row items-center justify-between gap-4 shadow-lg border border-slate-800">
        <div className="flex items-center gap-4">
          <div className="relative">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center border ${activeProvider ? 'bg-emerald-500/20 border-emerald-500/30' : 'bg-slate-800 border-slate-700'}`}>
              <SafeIcon icon={FiActivity} className={`${activeProvider ? 'text-emerald-400 animate-pulse' : 'text-slate-500'} text-xl`} />
            </div>
            {activeProvider && <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-emerald-500 border-2 border-slate-900 rounded-full"></div>}
          </div>
          <div>
            <h3 className="font-bold flex items-center gap-2">
              {activeProvider ? `System Live: ${activeProvider.provider_name || activeProvider.name}` : 'No Providers Configured'}
              {activeProvider && <span className="text-[10px] bg-blue-500 px-2 py-0.5 rounded uppercase">Primary</span>}
            </h3>
            <p className="text-slate-400 text-xs truncate max-w-[300px]">
              {activeProvider ? (activeProvider.notes || 'Routing traffic via primary provider.') : 'Register a provider to enable email routing.'}
            </p>
          </div>
        </div>
        <div className="flex gap-3 w-full md:w-auto">
          <div className="flex-1 md:flex-none px-4 py-2 bg-slate-800 rounded-lg border border-slate-700 text-center">
            <p className="text-[9px] text-slate-500 uppercase font-black tracking-widest">Deliverability</p>
            <p className="text-sm font-bold flex items-center justify-center gap-1.5">
              <SafeIcon icon={FiCheckCircle} className="text-emerald-400" /> 
              {stats.sent > 0 ? `${Math.round((stats.sent / (stats.sent + stats.failed)) * 100)}%` : '100%'}
            </p>
          </div>
        </div>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <KPI label="Total Clients" value={stats.totalClients} icon={FiUsers} loading={loading} />
        <KPI label="Active Apps" value={stats.activeApps} icon={FiBox} loading={loading} />
        <KPI label="Active Keys" value={stats.activeKeys} icon={FiKey} loading={loading} />
        <KPI label="Queued" value={stats.queued} icon={FiClock} loading={loading} />
        <KPI label="Sent" value={stats.sent} icon={FiSend} loading={loading} />
        <KPI label="Failed" value={stats.failed} status={stats.failed > 0 ? 'error' : 'normal'} icon={FiAlertCircle} loading={loading} />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quota Monitoring */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
          <div className="px-5 py-4 border-b border-slate-100 flex justify-between items-center">
            <h3 className="font-bold text-slate-800">Quota Watchlist</h3>
            <SafeIcon icon={FiZap} className="text-blue-500" />
          </div>
          <div className="p-5 flex-1 flex flex-col min-h-[250px]">
            {clientsWithQuotas.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center opacity-40">
                <SafeIcon icon={FiShield} className="text-4xl mb-2" />
                <p className="text-xs font-bold italic uppercase tracking-widest">No quota limits configured from live data.</p>
              </div>
            ) : (
              <div className="space-y-6">
                {clientsWithQuotas.map(client => (
                  <div key={client.id} className="space-y-2">
                    <div className="flex justify-between items-end">
                      <p className="text-sm font-bold text-slate-800">{client.client_name || client.name}</p>
                      <span className="text-[10px] text-slate-400 font-mono uppercase">{client.sent.toLocaleString()} / {client.monthlyLimit.toLocaleString()}</span>
                    </div>
                    <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden border border-slate-200/50">
                      <div className={`h-full rounded-full transition-all duration-1000 ${client.percent > 90 ? 'bg-red-500' : 'bg-blue-500'}`} style={{ width: `${client.percent}%` }}></div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Client Health */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
          <div className="px-5 py-4 border-b border-slate-100">
            <h3 className="font-bold text-slate-800">Client Operations Health</h3>
          </div>
          <div className="p-4 flex-1 overflow-y-auto max-h-[350px] custom-scrollbar">
            {data.clients.length === 0 ? (
              <p className="text-xs text-slate-400 italic text-center py-10">No live clients found.</p>
            ) : (
              <div className="divide-y divide-slate-50">
                {data.clients.map(client => (
                  <div key={client.id} className="py-3 flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="text-xs font-bold text-slate-800 truncate">{client.client_name || client.name}</p>
                      <p className="text-[10px] text-slate-400 uppercase tracking-widest">{client.industry || 'General'}</p>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase ${client.status === 'active' || client.status === 'enabled' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                      {client.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;